import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UserIcon, Mail, MapPin, Phone, Building2, Save, LogOut, ShieldCheck, CheckCircle2, MailWarning, Camera, Loader2, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/shared";
import { updateProfile, api } from "@/lib/api";
import { CommunitiesSection } from "@/components/CommunitiesSection";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Account() {
  const { currentUser, setCurrentUser, refreshUser, logout } = useApp();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: currentUser.name || "",
    location: currentUser.location || "",
    phone: currentUser.phone || "",
    avatar_url: currentUser.avatar_url || "",
    company: currentUser.company || "",
    headline: currentUser.headline || "",
    bio: currentUser.bio || "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const u = await updateProfile(form);
      setCurrentUser(u);
      await refreshUser();
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const onPickFile = () => fileInputRef.current?.click();
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) return toast.error("Use a JPG, PNG, WebP or GIF image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/avatar", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      set("avatar_url", data.url);
      const u = await refreshUser();
      if (u) setCurrentUser(u);
      toast.success("Avatar updated");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const removeAvatar = () => { set("avatar_url", ""); toast.info("Avatar removed. Save to keep the change."); };

  return (
    <div className="min-h-screen grain">
      <Header title="Account" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Your profile</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Account settings</h1>

        <div className="mt-6 rounded-lg border border-border bg-card p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative group">
              <Avatar className="w-24 h-24 border-2 border-border">
                <AvatarImage src={form.avatar_url} className="object-cover" />
                <AvatarFallback className="text-2xl">{currentUser.name?.[0]}</AvatarFallback>
              </Avatar>
              <button
                data-testid="avatar-upload-btn"
                type="button"
                onClick={onPickFile}
                disabled={uploading}
                aria-label="Upload avatar"
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-md hover:scale-105 transition-transform disabled:opacity-70"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <input
                ref={fileInputRef}
                data-testid="avatar-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={onFileChange}
              />
            </div>
            <div className="flex-1 w-full">
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">Profile photo</div>
              <p className="text-sm text-foreground/80 mt-1">
                Upload a JPG, PNG, WebP or GIF — up to 5 MB.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button data-testid="avatar-choose-btn" variant="outline" size="sm" onClick={onPickFile} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                  {uploading ? "Uploading…" : "Upload new photo"}
                </Button>
                {form.avatar_url && (
                  <Button data-testid="avatar-remove-btn" variant="ghost" size="sm" onClick={removeAvatar}>
                    <X className="w-4 h-4 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Full name" icon={<UserIcon className="w-4 h-4" />}>
              <Input data-testid="account-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field label="Email">
              <Input value={currentUser.email} disabled />
            </Field>
            <Field label="Location" icon={<MapPin className="w-4 h-4" />}>
              <Input data-testid="account-location" value={form.location} onChange={(e) => set("location", e.target.value)} />
            </Field>
            <Field label="Phone" icon={<Phone className="w-4 h-4" />}>
              <Input data-testid="account-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            {currentUser.role === "employer" && (
              <Field label="Company" icon={<Building2 className="w-4 h-4" />}>
                <Input data-testid="account-company" value={form.company} onChange={(e) => set("company", e.target.value)} />
              </Field>
            )}
            <Field label={currentUser.role === "worker" ? "Headline" : "Role / Title"}>
              <Input data-testid="account-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} />
            </Field>
          </div>

          {currentUser.role === "worker" && (
            <div className="mt-4">
              <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">Bio</Label>
              <Textarea data-testid="account-bio" rows={4} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Tell employers about yourself…" />
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3 items-center justify-between">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> Signed in as {currentUser.email}
              {currentUser.email_verified ? (
                <span data-testid="email-verified-pill" className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 font-bold">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              ) : (
                <VerifyPill />
              )}
            </div>
            <div className="flex gap-2">
              <Button data-testid="account-logout-btn" variant="outline" onClick={logout}><LogOut className="w-4 h-4 mr-1.5" /> Sign out</Button>
              <Button data-testid="account-save-btn" onClick={save} disabled={busy}><Save className="w-4 h-4 mr-1.5" /> Save changes</Button>
            </div>
          </div>
        </div>

        <CommunitiesSection userRole={currentUser.role} />
      </div>
    </div>
  );
}

const Field = ({ label, icon, children }) => (
  <div>
    <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-1.5">{icon}{label}</Label>
    {children}
  </div>
);

const VerifyPill = () => {
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState("");
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email/resend");
      if (data.already_verified) toast.info("Email already verified");
      else if (data.sent) toast.success("Verification email sent");
      else if (data._dev_verify_token) {
        setDevLink(`/verify-email?token=${data._dev_verify_token}`);
        toast.info("Email not configured — use the demo link");
      }
    } catch { toast.error("Could not resend"); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-2">
      <span data-testid="email-unverified-pill" className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-2 py-0.5 font-bold">
        <MailWarning className="w-3 h-3" /> Unverified
      </span>
      <button data-testid="resend-verify-btn" onClick={resend} disabled={busy}
              className="underline text-primary hover:opacity-80">Resend link</button>
      {devLink && <a data-testid="dev-verify-link" href={devLink} className="underline text-primary">Open demo link</a>}
    </span>
  );
};
